import AddIcon from '@mui/icons-material/Add';
import { Button, Grid, Typography } from '@mui/material';
import { ReactNode } from 'react';
import { SidebarAccordion } from 'src/components/accordion';
import { InputTextLabel } from 'src/components/input/Input.styled';
import { InputWidget } from 'src/components/widget';
import { InputTextField } from 'src/features/components/addComponentPage/AddComponentPage.styled';
import { lang } from 'src/utils';

import { IPositionGridLines } from '../../ScheduleComponentPage.interface';
import {
  AccordionTitle,
  AccordionTitleWrapper,
  PlayArrowIconStyled,
  StartIcon,
  StopIconStyled,
  StyledContainer,
  StyledLinkTypography,
} from './PositionAccordion.styled';

export interface IPositionAccordion {
  scheduleName: string;
  daypartName: string;
  positions: IPositionGridLines[];
  addNewPosition: () => void;
  previewingPositionIndex: number;
  onPreviewContent: (positionIndex: number, isPreviewing: boolean) => void;
  onUpdatePositionDuration: (duration: number, positionIndex: number) => void;
  getExtraContent?: (positionIndex: number) => ReactNode;
  handleScheduleNameChange: (name: unknown) => void;
  options?: {
    enabledScheduleName?: boolean;
  };
  validateScheduleName: (name: string) => string | false;
  positionRefs: React.MutableRefObject<{
    [key: number]: HTMLDivElement | null;
  }>;
}

export interface IPositionAccordionContentProps
  extends Pick<IPositionAccordion, 'getExtraContent'> {
  positionIndex: number;
  isPreviewing: boolean;
  duration: number;
  options?: {
    enableDurationInput?: boolean;
  };
  onClickPreviewButton: () => void;
  onChangeDuration: (value: number, positionIndex: number) => void;
}

export const PositionAccordionTitle = (props: { position: number }) => {
  const { position } = props;

  return (
    <AccordionTitleWrapper>
      <StartIcon>{position}</StartIcon>
      <AccordionTitle variant="body1">{`${lang.position} ${position}`}</AccordionTitle>
    </AccordionTitleWrapper>
  );
};

export const PositionAccordionContent = (
  props: IPositionAccordionContentProps,
) => {
  const {
    positionIndex,
    isPreviewing,
    duration,
    options = { enableDurationInput: true },
    onClickPreviewButton,
    getExtraContent,
    onChangeDuration,
  } = props;

  const handleChangeDuration = (value: unknown) => {
    const numValue = value as number;
    const regex = /^[1-9][0-9]*$/;
    if (regex.test(numValue.toString())) {
      onChangeDuration(numValue as number, positionIndex);
    }
  };

  return (
    <Grid container gap={2} direction="column" padding={2} paddingBottom={4}>
      <Grid
        container
        gap={1}
        alignItems="center"
        wrap="nowrap"
        onClick={() => onClickPreviewButton()}
        sx={{
          cursor: 'pointer',
        }}>
        <Grid item height={24}>
          {isPreviewing ? <StopIconStyled /> : <PlayArrowIconStyled />}
        </Grid>
        <Grid item flex={1}>
          <StyledLinkTypography fontWeight="400" $active={isPreviewing}>
            {lang.scheduleComponent.previewPosition}
          </StyledLinkTypography>
        </Grid>
      </Grid>

      <Grid item>
        <InputTextLabel
          fontWeight="400"
          variant="subtitle1"
          color="textSecondary">
          {lang.duration}
        </InputTextLabel>
        <InputTextField
          type="number"
          autoComplete="off"
          variant="outlined"
          value={duration / 1000}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
            handleChangeDuration(event.target.value)
          }
          disabled={!options.enableDurationInput}
          size="small"
          inputProps={{
            min: 1,
            max: '999999',
            autoComplete: 'off',
            form: {
              autoComplete: 'off',
            },
          }}
        />
      </Grid>

      {getExtraContent?.(positionIndex)}
    </Grid>
  );
};

export const PositionAccordion = (props: IPositionAccordion) => {
  const {
    scheduleName,
    daypartName,
    positions,
    previewingPositionIndex,
    onPreviewContent,
    onUpdatePositionDuration,
    getExtraContent,
    handleScheduleNameChange,
    options,
    validateScheduleName,
    positionRefs,
  } = props;

  const scheduleNameError = validateScheduleName(scheduleName);

  const positionAccordionItems = positions.map(
    ({ position, duration }, index) => {
      const posIndex = index + 1;

      return {
        titleComponent: <PositionAccordionTitle position={position} />,
        content: (
          <div
            ref={(el) => (positionRefs.current[posIndex] = el)}
            data-testid={`position-${posIndex}`}>
            <PositionAccordionContent
              positionIndex={posIndex}
              isPreviewing={previewingPositionIndex === posIndex}
              duration={duration}
              onClickPreviewButton={() =>
                onPreviewContent(
                  posIndex,
                  !(posIndex === previewingPositionIndex),
                )
              }
              onChangeDuration={onUpdatePositionDuration}
              getExtraContent={getExtraContent}
            />
          </div>
        ),
        defaultExpanded: true,
      };
    },
  );

  const accordionItems = [
    {
      label: lang.generalDetails,
      content: (
        <Grid container padding={2} paddingBottom={4}>
          <Grid item width="100%">
            <InputWidget
              label={
                <Typography color="text.secondary">
                  {lang.scheduleComponent.scheduleNameField}
                </Typography>
              }
              disabled={!options?.enabledScheduleName}
              value={scheduleName}
              onChange={handleScheduleNameChange}
              hasError={!!scheduleNameError}
              error={scheduleNameError}
            />
          </Grid>

          <Grid item width="100%">
            <InputWidget
              label={
                <Typography color="text.secondary">{lang.daypart}</Typography>
              }
              disabled
              value={daypartName}
              onChange={() => {}}
            />
          </Grid>
        </Grid>
      ),
      defaultExpanded: true,
    },
    ...positionAccordionItems,
  ];

  return (
    <>
      <SidebarAccordion items={accordionItems} />
      <StyledContainer container>
        <Button
          variant="text"
          onClick={props.addNewPosition}
          size="small"
          startIcon={<AddIcon />}
          disabled={positions.length >= 15}
          fullWidth>
          {lang.addNewPosition}
        </Button>
      </StyledContainer>
    </>
  );
};
